import React from 'react';
import {
  Box,
  Container,
  Typography,
  Divider,
  Grid,
  Link as MuiLink,
  Stack,
  IconButton
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import InstagramIcon from '@mui/icons-material/Instagram';
import FavoriteIcon from '@mui/icons-material/Favorite';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { label: 'All Groups', persianLabel: 'تمام گروه ها', path: '/search' },
    { label: 'Terms & Conditions', persianLabel: 'قوانین و شرایط چیستا', path: '/cheesta-terms' },
    { label: 'Cheesta Help', persianLabel: 'راهنمای چیستا', path: '/cheesta-help' },
    { label: 'Pricing', persianLabel: 'تعرفه‌ها و آگهی ویژه', path: '/pricing' },
    { label: 'About Cheesta', persianLabel: 'درباره چیستا', path: '/about-cheesta-iranain-australia' },
    { label: 'Contact Us', persianLabel: 'تماس با ما', path: '/contact' },
    { label: 'IT Consultation', persianLabel: '\u2067دریافت مشاوره IT\u2069', path: '/hire-us' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#2854a1',
        color: '#ffffff',
        pt: 6,
        pb: 3,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ direction: 'rtl', textAlign: 'right' }}>
          {/* Brand and Description */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Cheesta Australia
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.8, mb: 2 }}>
              چیستا جامع‌ترین دایرکتوری مشاغل و متخصصین ایرانی در استرالیا است.
              هدف ما کمک به جامعه برای یافتن و ارتباط با خدمات محلی مورد اعتماد در سراسر استرالیا است.
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              support@cheesta.com.au
            </Typography>
          </Grid>

          {/* Quick Links */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              لینک های مفید
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {footerLinks.map((link) => (
                <Box component="li" key={link.path} sx={{ mb: 1 }}>
                  <MuiLink
                    component={RouterLink}
                    to={link.path}
                    sx={{
                      color: 'inherit',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      opacity: 0.8,
                      fontWeight: 400,
                      ...(link.path === '/hire-us' && {
                        display: 'inline-block',
                        bgcolor: 'rgba(255,255,255,0.1)',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5,
                        border: '1px solid rgba(255,255,255,0.2)',
                        opacity: 1,
                        mt: 0.5
                      }),
                      '&:hover': { 
                        opacity: 1, 
                        textDecoration: link.path === '/hire-us' ? 'none' : 'underline',
                        bgcolor: link.path === '/hire-us' ? 'rgba(255,255,255,0.2)' : 'transparent'
                      }
                    }}
                  >
                    {link.persianLabel}
                  </MuiLink>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Connect */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              با ما در ارتباط باشید
            </Typography>
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-start', mb: 2 }}>

              <IconButton
                component="a"
                href="https://www.instagram.com/cheesta_australia/"
                target="_blank"
                sx={{ color: '#ffffff', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                <InstagramIcon />
              </IconButton>
            </Stack>
            <Typography variant="caption" sx={{ opacity: 0.6, display: 'block' }}>
              ما را در شبکه های اجتماعی دنبال کنید
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ opacity: 0.7, fontSize: '0.75rem', direction: 'ltr' }}>
            Copyright {currentYear} Cheesta Australia, All Rights Reserved
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center', opacity: 0.7, fontSize: '0.75rem', direction: 'rtl' }}>
            <Typography variant="caption" sx={{ fontSize: 'inherit', color: 'inherit' }}>
              ساخته شده با
            </Typography>
            <FavoriteIcon sx={{ color: '#ff0000', fontSize: '1rem', mx: 0.2 }} />
            <Typography variant="caption" sx={{ fontSize: 'inherit', color: 'inherit' }}>
              توسط گروه
            </Typography>
            <MuiLink
              href="https://mohasys.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'inherit',
                fontSize: 'inherit',
                textDecoration: 'none',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              مهندسی چیستا
            </MuiLink>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
